import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Element, scroller } from 'react-scroll';
import numeral from 'numeral';
import { Dropdown, MenuItem, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import {
  find, first, map, indexOf, findIndex, camelCase, sortBy, findLast,
  without, concat, each, filter, defer, reduce, intersection, maxBy,
} from 'lodash';
import Fa from 'react-fontawesome';
import {
  getOpportunities, getStages, getOpportunityAmounts,
  getOpportunityDefaultAmountBounds,
  getConversionGraphLoading, getConversionGraphLoaded, getConversionGraphAverage,
} from '../../PipelineReducer';
import { getTypeColorFromKey } from '../../../../util/helpers';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import DropdownRange from '../../../../components/dropdown-range';
import DateTimeDisplay from '../../../../components/date-time-display';
import NumberDisplay from '../../../../components/number-display';
import MoneyDisplay from '../../../../components/money-display';
import chartStyles from '../brenchmark-graph/chart-styles.css';
import WindowResizeListener from '../../../../components/window-size-listener';
import CircleGraph from '../../../../components/circle-graph';


const stageOptions = [
  { label: 'Current', value: 'current' },
  { label: 'Early Stage', value: 'early' },
  { label: 'Late Stage', value: 'late' },
];
const typeOptions = [
  { label: 'Amount', value: 'amount' },
  { label: 'Count', value: 'count' },
];
const graphOptions = [
  { label: 'Growth', value: 'growth' },
  { label: 'Age', value: 'age' },
  { label: 'Distribution', value: 'distribution' },
  { label: 'Progress', value: 'progress' },
];
const dealTypeOptions = [
  { label: 'Renewal', value: 'Renewal' },
  { label: 'New Business', value: 'New business' },
  { label: 'Add-on', value: 'Add-on' },
  { label: 'Upsell', value: 'Upsell' },
];

function getStageCategories(graphType) {
  if (graphType === 'growth') {
    return [
      { label: 'Current' },
      { label: 'Increase' },
      { label: 'Decrease' },
    ];
  } else if (graphType === 'age') {
    return [
      { label: 'Current' },
      { label: 'New' },
      { label: 'Old' },
    ];
  } else if (graphType === 'distribution') {
    return [
      { label: 'Small' },
      { label: 'Medium' },
      { label: 'Large' },
    ];
  } else if (graphType === 'progress') {
    return [
      { label: 'Current' },
      { label: 'Advanced' },
      { label: 'Regressed' },
    ];
  }
  return [];
}

function getStageCategoryTooltip(stage, category, graphType, pipelineDate, lowerAmount, upperAmount, isTotal) {
  const dateComp = (
    <DateTimeDisplay value={pipelineDate} format="short" />
  );
  if (graphType === 'growth') {
    if (category === 'Current') {
      if (isTotal) {
        return (
          <span>Total pipeline based on selected filters</span>
        );
      }
      return (
        <span>Total pipeline in the <strong>{stage}</strong> stage</span>
      );
    } else if (category === 'Increase') {
      return (
        <span>Opportunities that have increased in size since {dateComp}</span>
      );
    } else if (category === 'Decrease') {
      return (
        <span>Opportunities that have decreased in size since {dateComp}</span>
      );
    }
  } else if (graphType === 'age') {
    if (category === 'Current') {
      if (isTotal) {
        return (
          <span>Total pipeline based on selected filters</span>
        );
      }
      return (
        <span>Total pipeline in the <strong>{stage}</strong> stage</span>
      );
    } else if (category === 'New') {
      return (
        <span>New opportunities added since {dateComp}</span>
      );
    } else if (category === 'Old') {
      return (
        <span>Opportunities that have spent at least 10% more time in the <strong>{stage}</strong> stage than is typically spent when you win</span>
      );
    }
  } else if (graphType === 'distribution') {
    if (category === 'Small') {
      return (
        <span>Opportunities between $0 and <MoneyDisplay value={lowerAmount} abbreviated precision={1} /></span>
      );
    } else if (category === 'Medium') {
      return (
        <span>Opportunities between <MoneyDisplay value={lowerAmount} abbreviated precision={1} /> and <MoneyDisplay value={upperAmount} abbreviated precision={1} /></span>
      );
    } else if (category === 'Large') {
      return (
        <span>Opportunities larger than <MoneyDisplay value={upperAmount} abbreviated precision={1} /></span>
      );
    }
  } else if (graphType === 'progress') {
    if (category === 'Current') {
      if (isTotal) {
        return (
          <span>Total pipeline based on selected filters</span>
        );
      }
      return (
        <span>Total pipeline in the <strong>{stage}</strong> stage</span>
      );
    } else if (category === 'Advanced') {
      return (
        <span>Opportunities that have advanced one or more stages since {dateComp}</span>
      );
    } else if (category === 'Regressed') {
      return (
        <span>Opportunities that have moved backward one or more stages since {dateComp}</span>
      );
    }
  }
  return null;
}


const USER_PREFERENCE_REPORT_TYPE = 'pipeline-size-graph-report-type';
const USER_PREFERENCE_STAGE = 'pipeline-size-graph-stage';
const USER_PREFERENCE_GRAPH_TYPE = 'pipeline-size-graph-graph-type';


class SizeGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stage: 'current',
      reportType: 'amount',
      graphType: 'growth',
      graphSeries: [], // remove this
      activeDealTypes: map(dealTypeOptions, 'value'),
      bounds: props.defaultAmountBounds,
      maxY: 0, // remove this

      chartData: [], // rows
      maxValue: 0,
      maxTotalValue: 0,
      containerWidth: 0,
    };
    this.setStage = this.setStage.bind(this);
    this.setReportType = this.setReportType.bind(this);
    this.setGraphType = this.setGraphType.bind(this);
    this.onSliderChange = this.onSliderChange.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    const graphType = userPreferences[USER_PREFERENCE_GRAPH_TYPE] || 'growth';
    const stage = userPreferences[USER_PREFERENCE_STAGE] || 'current';

    const data = this.gatherData({
      ...this.state, stage, reportType, graphType,
    });
    this.setState({
      stage, reportType, graphType, ...data,
    });
  }

  onWindowResize() {
    if (this.container) {
      const containerWidth = this.container.offsetWidth;
      this.setState({ containerWidth });
    }
  }

  onSliderChange(bounds) {
    const data = this.gatherData({ ...this.state, bounds });
    this.setState({ bounds, ...data });
  }

  setStage(stage) {
    const data = this.gatherData({ ...this.state, stage });
    this.setState({ stage, ...data });
    this.context.updateUserPreference(USER_PREFERENCE_STAGE, stage);
  }
  setReportType(reportType) {
    const data = this.gatherData({ ...this.state, reportType });
    this.setState({ reportType, ...data });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }
  setGraphType(graphType) {
    const data = this.gatherData({ ...this.state, graphType });
    this.setState({ graphType, ...data });
    this.context.updateUserPreference(USER_PREFERENCE_GRAPH_TYPE, graphType);
  }

  gatherData({
    stage, reportType, graphType, activeDealTypes, bounds,
  }) {
    const { opportunities, stages, amounts } = this.props;
    const lowerAmount = amounts[bounds[0]];
    const upperAmount = amounts[bounds[1]];
    const filteredStages = filter(stages, s => stage === 'current' || stage === s.conversion_stage);
    const categories = getStageCategories(graphType);
    const lastStage = findLast(stages, { skip_conversion: false });
    const totalRows = reduce(categories, (items, category, index) => {
      items.push({
        id: camelCase(`total ${category.label}`),
        isFirst: index === 0,
        isTotal: true,
        stage: {
          label: 'Total Pipeline',
        },
        category,
        tooltip: getStageCategoryTooltip('Total Pipeline', category.label, graphType, this.props.pipelineDate, lowerAmount, upperAmount, true),
        total: 0,
        stats: {}, // { 'add-on': { value: 1, ids: [] } }
      });
      return items;
    }, []);

    const rows = reduce(filteredStages, (items, filteredStage, stageIndex) => {
      const nextStage = filteredStages[stageIndex + 1];
      each(categories, (category, index) => {
        items.push({
          id: camelCase(`${filteredStage.stage_id} ${category.label}`),
          isFirst: index === 0,
          stage: { ...filteredStage, is_last_stage: lastStage ? lastStage._id === filteredStage._id : false },
          nextStageLabel: nextStage ? nextStage.label : null,
          category,
          tooltip: getStageCategoryTooltip(filteredStage.label, category.label, graphType, this.props.pipelineDate, lowerAmount, upperAmount),
          total: 0,
          stats: {}, // { 'add-on': { value: 1, ids: [] } }
        });
      });
      return items;
    }, []);
    const indexedFirstRows = reduce(rows, (items, row, index) => {
      if (row.isFirst) {
        items[row.stage.value] = index;
      }
      return items;
    }, {});
    each(opportunities, (opportunity) => {
      const stageName = opportunity.stage_name;
      const opportunityId = opportunity.opportunity_id;
      const stageFirstRowIndex = indexedFirstRows[stageName];
      const amount = opportunity.amount || 0;
      const incr = reportType === 'amount' ? amount : 1;
      if (stageFirstRowIndex >= 0) {
        const dealTypes = intersection(opportunity.types, activeDealTypes);
        let isFirstRow = null;
        let isSecondRow = null;
        let isThirdRow = null;

        if (graphType === 'growth') {
          isSecondRow = opportunity.delta_amount > 0;
          isThirdRow = opportunity.delta_amount < 0;
        } else if (graphType === 'progress') {
          isSecondRow = opportunity.delta_stage_order > 0;
          isThirdRow = opportunity.delta_stage_order < 0;
        } else if (graphType === 'age') {
          isSecondRow = opportunity.is_stage_new;
          isThirdRow = !opportunity.is_stage_new;
        } else if (graphType === 'distribution') {
          isSecondRow = amount > lowerAmount && amount < upperAmount; // medium
          isThirdRow = amount >= upperAmount; // large
        }

        if (graphType === 'distribution') {
          if (isSecondRow === false && isThirdRow === false) {
            isFirstRow = true;
          }
        // } else if (incr > 0 && (isSecondRow === true || isThirdRow === true)) {
        } else {
          isFirstRow = true; // first row shows everything
        }
        const indexIncrements = [];
        if (isFirstRow) {
          indexIncrements.push(0);
        }
        if (isSecondRow) {
          indexIncrements.push(1);
        }
        if (isThirdRow) {
          indexIncrements.push(2);
        }
        each(dealTypes, (dealType) => {
          each(indexIncrements, (indexIncrement) => {
            const stageRow = rows[stageFirstRowIndex + indexIncrement];
            const totalRow = totalRows[indexIncrement];
            each([stageRow, totalRow], (row) => {
              if (row) {
                const { stats } = row;
                if (stats) {
                  if (!stats[dealType]) {
                    stats[dealType] = {
                      value: 0, ids: [], amount: 0, count: 0,
                    };
                  }
                  stats[dealType].value += incr;
                  stats[dealType].count += 1;
                  stats[dealType].amount += amount;
                  stats[dealType].ids.push(opportunityId);
                  row.total += incr;
                }
              }
            });
          });
        });
      }
    });
    const maxRow = maxBy(rows, 'total');
    const maxValue = maxRow ? maxRow.total : 0;

    const maxTotalRow = maxBy(totalRows, 'total');
    const maxTotalValue = maxTotalRow ? maxTotalRow.total : 0;

    const allRows = concat(totalRows, rows);

    allRows.splice(0, 0, {
      rowType: 'total', id: 'total', total: 3, label: '',
    });
    const earlyStages = filter(allRows, row => row.stage !== undefined && row.stage.conversion_stage === 'early');
    const earlyStageIndex = findIndex(allRows, row => row.stage !== undefined && row.stage.conversion_stage === 'early');
    if (earlyStageIndex >= 0) {
      allRows.splice(earlyStageIndex, 0, {
        rowType: 'early', id: 'early', total: earlyStages.length, label: 'Early Stages',
      });
    }

    const lateStages = filter(allRows, row => row.stage !== undefined && row.stage.conversion_stage === 'late');
    const lateStageIndex = findIndex(allRows, row => row.stage !== undefined && row.stage.conversion_stage === 'late');
    if (lateStageIndex >= 0) {
      allRows.splice(lateStageIndex, 0, {
        rowType: 'late', id: 'late', total: lateStages.length, label: 'Late Stages',
      });
    }

    this.setState({ chartData: allRows, maxValue, maxTotalValue });
  }

  toggleDealType(dealType) {
    const { activeDealTypes } = this.state;
    let newActiveDealTypes = [...activeDealTypes];

    if (indexOf(newActiveDealTypes, dealType) >= 0) {
      // remove
      newActiveDealTypes = without(newActiveDealTypes, dealType);
    } else {
      newActiveDealTypes = concat(newActiveDealTypes, dealType);
    }
    const data = this.gatherData({ ...this.state, activeDealTypes: newActiveDealTypes });
    this.setState({ activeDealTypes: newActiveDealTypes, ...data });
    defer(() => {
      scroller.scrollTo('pipeline-size-chart', { duration: 500, smooth: true, offset: -70 });
    });
  }

  renderControls() {
    const {
      stage, reportType, graphType, bounds,
    } = this.state;
    const { organization, amounts } = this.props;
    const activeStage = find(stageOptions, { value: stage }) || first(stageOptions);
    const activeType = find(typeOptions, { value: reportType }) || first(typeOptions);
    const activeGraph = find(graphOptions, { value: graphType }) || first(graphOptions);
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    return (
      <h5 className="sub-header-with-controls">
        Analyze&nbsp;
        <Dropdown
          id="stage-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activeStage.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {stageOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activeStage.value}
                onSelect={() => { this.setStage(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;pipeline&nbsp;
        <Dropdown
          id="type-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activeType.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {typeOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activeType.value}
                onSelect={() => { this.setReportType(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;and show&nbsp;
        <Dropdown
          id="graph-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activeGraph.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {graphOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activeGraph.value}
                onSelect={() => { this.setGraphType(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        {graphType === 'distribution' &&
          <span>
            &nbsp;with boundaries&nbsp;
            <DropdownRange
              values={amounts}
              bounds={bounds}
              onSliderChange={this.onSliderChange}
              modalProps={{
                fieldPrefix: symbol,
                fieldSuffix: suffix,
                title: 'Small, Medium and Large Deal Size Boundaries',
                lowerLabel: 'Medium Size Start',
                upperLabel: 'Large Size Start',
              }}
            />
          </span>
        }
      </h5>
    );
  }

  renderDealTypeToggle() {
    const { activeDealTypes } = this.state;
    return (
      <div className={chartStyles.legend}>
        <ul>
          {dealTypeOptions.map((dealType) => {
            const active = indexOf(activeDealTypes, dealType.value) >= 0;
            return (
              <li
                className={active ? chartStyles.active : null}
                key={dealType.value}
                onClick={(e) => {  // eslint-disable-line
                  e.preventDefault();
                  e.stopPropagation();
                  this.toggleDealType(dealType.value);
                }}
              >
                <Fa name="circle" style={{ color: active ? getTypeColorFromKey(dealType.value) : null }} /> {dealType.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  renderRowBars(row) {
    const {
      reportType, maxValue, maxTotalValue, containerWidth,
    } = this.state;
    const { total, isTotal } = row;
    const min = 50; // pixel
    const bars = reduce(row.stats, (items, typeStat, dealName) => {
      const { value, ids } = typeStat;
      items.push({
        ...typeStat,
        ids,
        name: dealName,
        percentage: total !== 0 ? value / total : 0,
        color: getTypeColorFromKey(dealName),
        value,
      });
      return items;
    }, []);
    const sortedBar = sortBy(bars, [(bar) => {
      if (bar.name === 'Renewal') {
        return 10;
      } else if (bar.name === 'New business') {
        return 100;
      } else if (bar.name === 'Add-on') {
        return 1000;
      } else if (bar.name === 'Upsell') {
        return 10000;
      }
      return 100000;
    }]);
    let projected = 1;
    if (isTotal) {
      projected = maxTotalValue !== 0 ? total / maxTotalValue : 0;
    } else {
      projected = maxValue !== 0 ? total / maxValue : 0;
    }

    const tip = (
      <Tooltip
        id={camelCase(`size popover ${row.stage.value}`)}
        className={chartStyles.tooltip}
      >
        <div>
          <h1 className={chartStyles.tooltipTitle}>{row.category.label}<sup>*</sup></h1>
          <h2 className={chartStyles.tooltipSubTitle}>
            {isTotal && <span>{row.stage.label}</span>}
            {!isTotal && <span>Stage: {row.stage.label}</span>}
          </h2>
          <table className="table table-condensed">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th className="text-right">Count</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Percent</th>
              </tr>
            </thead>
            <tbody>
              {sortedBar.map((bar) => {
                const per = total !== 0 ? bar[reportType] / total : 0;
                return (
                  <tr key={bar.name}>
                    <td style={{ color: bar.color }}>
                      {bar.name}
                    </td>
                    <td className="text-right">
                      <NumberDisplay value={bar.count} precision={0} />
                    </td>
                    <td className="text-right">
                      <nobr><MoneyDisplay value={bar.amount} abbreviated precision={2} /></nobr>
                    </td>
                    <td className="text-right">
                      <NumberDisplay value={per * 100} suffix="%" precision={1} />
                    </td>
                  </tr>
                  );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4"><sup>*</sup> {row.tooltip}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Tooltip>
    );
    return [
      (
        <td key="label" className="text-right">
          <span>{row.category.label}</span>
        </td>
      ),
      (
        <td
          key="bars"
        >
          {sortedBar.length > 0 &&
            <OverlayTrigger trigger={['hover']} placement="top" overlay={tip}>
              <ProgressBar
                className={chartStyles.progress}
              >
                {sortedBar.map((bar) => {
                  const now = bar.percentage * 100;
                  const label = numeral(bar.percentage).format('0.0%');
                  return (
                    <ProgressBar
                      now={now * projected}
                      label={bar.percentage * projected * containerWidth > min ? label : null}
                      key={bar.name}
                      style={{ backgroundColor: bar.color }}
                      onClick={() => {
                        this.props.onSelect(bar.ids);
                      }}
                    />
                  );
                })}
              </ProgressBar>
            </OverlayTrigger>
          }
          {sortedBar.length === 0 &&
            <ProgressBar
              className={`${chartStyles.progress} ${chartStyles.progressDisabled}`}
            >
              <ProgressBar now={0} />
            </ProgressBar>
          }
        </td>
      ),
      (
        <td
          key="total"
          className="text-right"
        >
          {reportType === 'amount' && <MoneyDisplay value={row.total} abbreviated precision={1} />}
          {reportType === 'count' && <NumberDisplay value={row.total} precision={0} suffix="Deal" suffixIsUnit />}
        </td>
      ),
    ];
  }

  renderCircle(row, grandTotal) {
    const { conversionGraphLoaded, conversionGraphLoading, conversionGraphStages } = this.props;
    const { reportType } = this.state;
    const {
      stage, isTotal, total,
    } = row;
    const isLastStage = stage.is_last_stage === true;
    const isLoading = conversionGraphLoading && !conversionGraphLoaded;
    let tooltip = null;
    let title = null;
    let percentage = 0;

    if (!isLoading) {
      if (isTotal) {
        let masterWinRate = 1;
        each(conversionGraphStages, (filteredStage) => {
          if (!filteredStage.skip_conversion) {
            masterWinRate *= reportType === 'amount' ? filteredStage.averageWonAmountRate : filteredStage.averageWonCountRate;
          }
        });
        percentage = masterWinRate;
      } else {
        const conversionStage = find(conversionGraphStages, { value: stage.value });
        if (conversionStage) {
          percentage = reportType === 'amount' ? conversionStage.averageWonAmountRate : conversionStage.averageWonCountRate;
        }
      }
      title = numeral(percentage).format('0.0%');
      const rateByTotal = percentage * grandTotal;

      const totalComp = reportType === 'amount' ?
        <MoneyDisplay value={rateByTotal} precision={1} abbreviated /> :
        <NumberDisplay value={rateByTotal} precision={0} suffix="deal" suffixIsUnit />;

      let tailStr = '';
      if (!isTotal) {
        tailStr = !isLastStage ? (<span>will progress to <strong>{row.nextStageLabel}</strong></span>) : <span>will be closed as won.</span>;
      } else {
        tailStr = reportType === 'amount' ? 'of the pipeline amount pursued' : 'of opportunities pursued';
      }
      tooltip = (
        <div className={chartStyles.tooltip}>
          <div className="text-left">
            {isTotal &&
              <div>
                Based on past performance, you win <strong>{title}</strong> {tailStr}
              </div>
            }
            {!isTotal &&
              <div>
                Based on past performance, <strong>{title}</strong> of the pipeline
                in <strong>{stage.label}</strong>, or <strong>{totalComp}</strong> {tailStr}
              </div>
            }
          </div>
        </div>
      );
    } else {
      title = '<i class="fa fa-spinner fa-spin text-muted" ></i>';
    }

    return (
      <div className={`${chartStyles.circle} ${isTotal ? chartStyles.totalCircle : ''}`}>
        <CircleGraph
          title={title}
          color="#2196f3"
          value={percentage}
          tooltip={tooltip}
          subTitle={isTotal ? 'Win Rate' : 'Conversion Rate'}
          half
        />
      </div>
    );
  }

  renderSkippedRow(row) {
    const tip = (
      <Tooltip
        id={camelCase(`skipped ${row.stage.value}`)}
        className={chartStyles.tooltip}
      >
        <strong>{row.stage.label}</strong> stage is excluded from all calculations.
      </Tooltip>
    );
    return [
      (
        <td
          key="stage"
          colSpan="6"
          style={{ verticalAlign: 'middle' }}
        >
          <OverlayTrigger trigger={['hover']} placement="top" overlay={tip}>
            <span className={`${chartStyles.stageName} text-muted`}>{row.stage.label} <Fa name="exclamation-triangle" /></span>
          </OverlayTrigger>
        </td>
      ),
    ];
  }

  renderGraph() {
    const { chartData, graphType } = this.state;
    return (
      <div>
        <table className={`table table-condensed ${chartStyles.mainTable}`}>
          <thead>
            <tr>
              <th />
              <th />
              <th />
              <th width="99%">
                <div ref={(el) => { this.container = el; }}>
                  <WindowResizeListener onResize={this.onWindowResize} />
                </div>
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {chartData.map((row, idx) => {
              const {
                rowType, id, isFirst, isTotal,
              } = row;

              let grandTotal = row.total;
              if (graphType === 'distribution') {
                const nextRow = chartData[idx + 1];
                const nextNextRow = chartData[idx + 2];
                if (nextRow) {
                  grandTotal += nextRow.total || 0;
                }
                if (nextNextRow) {
                  grandTotal += nextNextRow.total || 0;
                }
              }

              if (rowType === 'early' || rowType === 'late' || rowType === 'total') {
                return [
                  (
                    <tr
                      key={`${id}-0`}
                      className={`${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td
                        colSpan={6}
                      >
                        &nbsp;
                      </td>
                    </tr>
                  ),
                  (
                    <tr
                      key={`${id}-1`}
                      className={`${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td rowSpan={row.total + 1} className={chartStyles.groupColumn}>
                        <span className={chartStyles.rotatedStage}>
                          <span className="label text-uppercase">
                            {row.label}
                          </span>
                        </span>
                      </td>
                    </tr>
                  ),
                ];
              }
              const isSkipped = row.stage.skip_conversion;
              const rowSpan = isSkipped ? 1 : 3;
              const cls = [];
              if (isFirst) {
                cls.push(chartStyles.divider);
              }
              if (isTotal) {
                cls.push(chartStyles.totalRow);
              }
              if (isSkipped) {
                cls.push(chartStyles.skippedRow);
              }

              return (
                <tr
                  key={id}
                  className={cls.join(' ')}
                >
                  {!isSkipped && isFirst &&
                    <td rowSpan={rowSpan} style={{ verticalAlign: 'middle' }}>
                      <span className={chartStyles.stageName}>{row.stage.label}</span>
                    </td>
                  }
                  {!isSkipped && this.renderRowBars(row)}
                  {isSkipped && isFirst && this.renderSkippedRow(row)}
                  {!isSkipped && isFirst &&
                    <td rowSpan={rowSpan} className="text-center" style={{ verticalAlign: 'middle' }}>
                      {this.renderCircle(row, grandTotal)}
                    </td>
                  }
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderControls()}
        <Element name="pipeline-size-chart" />
        {this.renderGraph()}
        {this.renderDealTypeToggle()}
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    stages: getStages(state),
    organization: getUserCurrentOrganization(state),
    amounts: getOpportunityAmounts(state),
    defaultAmountBounds: getOpportunityDefaultAmountBounds(state),
    conversionGraphLoading: getConversionGraphLoading(state),
    conversionGraphLoaded: getConversionGraphLoaded(state),
    conversionGraphStages: getConversionGraphAverage(state),
  };
}

SizeGraph.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({})),
  stages: PropTypes.arrayOf(PropTypes.shape({})),
  conversionGraphStages: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func,
  conversionGraphLoading: PropTypes.bool,
  conversionGraphLoaded: PropTypes.bool,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  amounts: PropTypes.arrayOf(PropTypes.number).isRequired,
  defaultAmountBounds: PropTypes.arrayOf(PropTypes.number).isRequired,
  pipelineDate: PropTypes.instanceOf(moment).isRequired,
};

SizeGraph.defaultProps = {
  opportunities: [],
  organization: null,
  conversionGraphLoading: false,
  conversionGraphLoaded: false,
  onSelect: () => {},
  stages: [],
  conversionGraphStages: [],
};
SizeGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({}).isRequired,
};
export default connect(mapStateToProps)(SizeGraph);
